import { useEffect, useRef, useState } from 'react';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import {
  formatExpiryOption,
  hasActiveSchoolFilters,
} from '../utils/adminSchoolFilters';

const selectClass =
  'w-full rounded-lg border border-border bg-card px-2.5 py-2 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

const panelClass =
  'absolute left-0 top-[calc(100%+6px)] z-40 rounded-xl border border-border bg-card shadow-card';

function useClickOutside(ref, onClose, enabled) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [enabled, onClose, ref]);
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
        active
          ? 'bg-primary text-white'
          : 'border border-border bg-surface text-muted hover:border-primary/40 hover:text-text'
      }`}
    >
      {children}
    </button>
  );
}

function FilterTrigger({ label, active, open, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
        open || active
          ? 'border-primary/40 bg-primary-light/50 text-primary'
          : 'border-border bg-card text-text hover:bg-surface'
      }`}
    >
      <span>{label}</span>
      <ChevronDown
        className={`h-4 w-4 shrink-0 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`}
      />
    </button>
  );
}

function getValidUntilSummary(labels, { validExact, validFrom, validTo, validPreset }) {
  if (validPreset === 'expired') return labels.filterValidPresetExpired;
  if (validPreset === 'expiring30') return labels.filterValidPreset30;
  if (validPreset === 'expiring14') return labels.filterValidPreset14;
  if (validExact) return formatExpiryOption(validExact);
  if (validFrom && validTo) {
    return `${formatExpiryOption(validFrom)} – ${formatExpiryOption(validTo)}`;
  }
  if (validFrom) return `${formatExpiryOption(validFrom)} →`;
  if (validTo) return `→ ${formatExpiryOption(validTo)}`;
  return null;
}

function getDivisionsSummary(labels, divisionsSort) {
  if (divisionsSort === 'desc') return labels.filterDivisionsHigh;
  if (divisionsSort === 'asc') return labels.filterDivisionsLow;
  return null;
}

function isValidUntilActive({ validExact, validFrom, validTo, validPreset }) {
  return Boolean(
    validExact || validFrom || validTo || (validPreset && validPreset !== 'all')
  );
}

export function SchoolHeadFilters({
  labels,
  search,
  onSearchChange,
  uniqueExpiryDates,
  validExact,
  setValidExact,
  validFrom,
  setValidFrom,
  validTo,
  setValidTo,
  validPreset,
  setValidPreset,
  divisionsSort,
  setDivisionsSort,
  shownCount,
  totalCount,
  onClear,
}) {
  const [openPanel, setOpenPanel] = useState(null);
  const barRef = useRef(null);
  useClickOutside(barRef, () => setOpenPanel(null), openPanel !== null);

  const filtersActive = hasActiveSchoolFilters({
    validExact,
    validFrom,
    validTo,
    validPreset,
    divisionsSort,
  });

  const validActive = isValidUntilActive({ validExact, validFrom, validTo, validPreset });
  const divisionsActive = divisionsSort !== 'none';

  const pickPreset = (preset) => {
    setValidPreset(preset);
    setValidExact('');
    setValidFrom('');
    setValidTo('');
  };

  const onExactChange = (value) => {
    setValidExact(value);
    setValidPreset('all');
    setValidFrom('');
    setValidTo('');
  };

  const onRangeChange = (which, value) => {
    if (which === 'from') setValidFrom(value);
    else setValidTo(value);
    setValidPreset('all');
    setValidExact('');
  };

  const presetOptions = [
    { value: 'all', label: labels.filterValidPresetAll },
    { value: 'expired', label: labels.filterValidPresetExpired },
    { value: 'expiring30', label: labels.filterValidPreset30 },
    { value: 'expiring14', label: labels.filterValidPreset14 },
  ];

  const divisionOptions = [
    { value: 'none', label: labels.filterDivisionsDefault },
    { value: 'desc', label: labels.filterDivisionsHigh },
    { value: 'asc', label: labels.filterDivisionsLow },
  ];

  const validSummary = getValidUntilSummary(labels, {
    validExact,
    validFrom,
    validTo,
    validPreset,
  });
  const divisionsSummary = getDivisionsSummary(labels, divisionsSort);

  return (
    <div ref={barRef} className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder={labels.search}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="min-w-[12rem] flex-1 sm:max-w-xs"
        />

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <FilterTrigger
              label={labels.subscriptionValidUntil}
              active={validActive}
              open={openPanel === 'valid'}
              onClick={() => setOpenPanel((p) => (p === 'valid' ? null : 'valid'))}
            />
            {openPanel === 'valid' && (
              <div className={`${panelClass} w-[min(calc(100vw-2rem),22rem)] p-3 sm:w-80`}>
                <div className="flex flex-wrap gap-1.5">
                  {presetOptions.map((opt) => (
                    <FilterChip
                      key={opt.value}
                      active={validPreset === opt.value && !validExact && !validFrom && !validTo}
                      onClick={() => pickPreset(opt.value)}
                    >
                      {opt.label}
                    </FilterChip>
                  ))}
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <label className="block space-y-1 sm:col-span-2">
                    <span className="text-xs text-muted">{labels.filterValidExact}</span>
                    <select
                      className={selectClass}
                      value={validExact}
                      onChange={(e) => onExactChange(e.target.value)}
                    >
                      <option value="">{labels.filterValidAnyDate}</option>
                      {uniqueExpiryDates.map((iso) => (
                        <option key={iso} value={iso}>
                          {formatExpiryOption(iso)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block space-y-1">
                    <span className="text-xs text-muted">{labels.filterValidFrom}</span>
                    <input
                      type="date"
                      className={selectClass}
                      value={validFrom}
                      onChange={(e) => onRangeChange('from', e.target.value)}
                    />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-xs text-muted">{labels.filterValidTo}</span>
                    <input
                      type="date"
                      className={selectClass}
                      value={validTo}
                      onChange={(e) => onRangeChange('to', e.target.value)}
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <FilterTrigger
              label={labels.filterDivisionsSort}
              active={divisionsActive}
              open={openPanel === 'divisions'}
              onClick={() => setOpenPanel((p) => (p === 'divisions' ? null : 'divisions'))}
            />
            {openPanel === 'divisions' && (
              <div className={`${panelClass} min-w-full w-48 p-1.5`}>
                {divisionOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setDivisionsSort(opt.value);
                      setOpenPanel(null);
                    }}
                    className={`flex w-full items-center rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                      divisionsSort === opt.value
                        ? 'bg-primary-light font-medium text-primary'
                        : 'text-text hover:bg-surface'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {filtersActive && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onClear();
                setOpenPanel(null);
              }}
              className="text-muted"
            >
              <X className="h-4 w-4" />
              <span className="hidden sm:inline">{labels.clearFilters}</span>
            </Button>
          )}
        </div>

        <p className="ml-auto text-xs text-muted tabular-nums">
          {labels.filterSchoolsCount
            .replace('{shown}', String(shownCount))
            .replace('{total}', String(totalCount))}
        </p>
      </div>

      {filtersActive && (
        <div className="flex flex-wrap items-center gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5 text-muted" aria-hidden />
          {validSummary && (
            <span className="rounded-full bg-primary-light px-2 py-0.5 text-xs font-medium text-primary">
              {labels.subscriptionValidUntil}: {validSummary}
            </span>
          )}
          {divisionsSummary && (
            <span className="rounded-full bg-primary-light px-2 py-0.5 text-xs font-medium text-primary">
              {labels.filterDivisionsSort}: {divisionsSummary}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
